import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InteractiveCard } from '@/components/ui/interactive-card';
import type { KnowledgeCategory, KnowledgeArticle } from '@/types/knowledgeBook';

type Props = {
  category: KnowledgeCategory | undefined;
  articles: KnowledgeArticle[];
  loading: boolean;
  onBack: () => void;
  onSelectArticle: (articleSlug: string) => void;
};

export default function ArticleListView({ category, articles, loading, onBack, onSelectArticle }: Props) {
  const { t } = useTranslation();
  if (!category) return null;

  return (
    <>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-6 gap-1.5 text-muted-foreground">
        <ArrowLeft className="size-4" />
        {t('knowledge.title')}
      </Button>

      <div className="mb-8 flex items-center gap-3">
        <div
          className="flex size-12 items-center justify-center rounded-xl text-2xl"
          style={{ backgroundColor: `${category.color}15` }}
        >
          {category.icon}
        </div>
        <div>
          <h1 className="text-2xl font-heading text-foreground">{category.title}</h1>
          <p className="text-sm text-muted-foreground">{category.description}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map((article, index) => (
            <InteractiveCard
              key={article.id}
              onClick={() => onSelectArticle(article.slug)}
              className="items-start rounded-xl p-4 sm:p-5"
            >
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: category.color }}
              >
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-foreground">{article.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{article.preview}</p>
              </div>
              <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
            </InteractiveCard>
          ))}
        </div>
      )}
    </>
  );
}
